import React, { useState } from 'react';
import { FaChevronDown, FaChevronUp, FaClock, FaTrash, FaEdit } from 'react-icons/fa';
import { OKR_STATUS_META } from '../../utils/okrStatusUtils';
import './ObjectiveCard.css';

export default function ObjectiveCard({ 
  okr, 
  onToggleExpand, 
  onToggleKR, 
  onEdit, 
  onDelete,
  isExpanded = false,
  canEdit = false,
  canDelete = false
}) {
  const meta = OKR_STATUS_META[okr.status] || {};
  const done = okr.keyResults?.filter(kr => kr.completed).length || 0;
  const total = okr.keyResults?.length || 0;
  const progress = okr.progress || 0;
  
  // Calculate deadline relative text
  const getDeadlineText = (deadline) => {
    if (!deadline) return { text: 'No deadline set', color: '#757575' };
    const due = new Date(deadline);
    const now = new Date();
    const diff = (due - now) / (1000 * 60 * 60 * 24);
    
    if (diff < -3) return { text: `Overdue by ${Math.abs(Math.floor(diff))} days`, color: '#D32F2F' };
    if (diff < 0) return { text: `Overdue by ${Math.abs(Math.floor(diff))} days`, color: '#F57C00' };
    if (diff <= 1) return { text: 'Due tomorrow', color: '#F57C00' };
    if (diff <= 3) return { text: `Due in ${Math.floor(diff)} days`, color: '#F57C00' };
    return { text: `Due in ${Math.floor(diff)} days`, color: '#2E7D32' };
  };
  
  const deadlineInfo = getDeadlineText(okr.deadline);
  const deadlineColor = okr.deadline ? 
    (new Date(okr.deadline) < new Date() ? '#D32F2F' : 
     (new Date(okr.deadline) - new Date()) / (1000 * 60 * 60 * 24) <= 2 ? '#F57C00' : '#2E7D32') : 
    '#757575';
  
  const renderOwners = () => {
    if (Array.isArray(okr.owners) && okr.owners.length) {
      return okr.owners.join(', ');
    }
    return okr.owner || '—';
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };
  
  const getScoreLabel = (score) => {
    if (score < 0.5) return 'Low';
    if (score < 0.8) return 'Medium';
    return 'High';
  };
  
  const scoreColor = okr.score < 0.5 ? '#D32F2F' : okr.score < 0.8 ? '#F57C00' : '#2E7D32';
  
  return (
    <div className="m3-objective-card" style={{ borderLeft: `4px solid ${meta.color || '#757575'}` }}>
      {/* Top Row - Title (LEFT) and Status/Actions (RIGHT) */}
      <div className="m3-objective-header">
        <div className="m3-objective-title-section" onClick={() => onToggleExpand(okr._id)}>
          <h3 className="m3-objective-title">{okr.objective}</h3>
          <div className="m3-objective-subtitle">
            <span className="m3-owners">Owners: {renderOwners()}</span>
            {okr.deadline && (
              <>
                <span className="m3-separator">•</span>
                <span className="m3-deadline">
                  Deadline: {formatDate(okr.deadline)}
                  <FaClock style={{ marginLeft: '4px', color: deadlineColor }} />
                </span>
                <span className="m3-deadline-relative" style={{ color: deadlineInfo.color }}>
                  {deadlineInfo.text}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="m3-objective-actions">
          <span 
            className="m3-status-pill"
            style={{
              background: meta.bg || '#F5F5F5',
              color: meta.color || '#757575',
              borderColor: meta.border || '#757575'
            }}
            title={meta.tooltip || okr.status}
          >
            <span>{meta.icon || '●'}</span>
            {okr.status}
          </span>
          {canEdit && (
            <button 
              className="m3-icon-button" 
              onClick={(e) => { e.stopPropagation(); onEdit(okr._id); }}
              title="Edit"
            >
              <FaEdit />
            </button>
          )}
          {canDelete && (
            <button 
              className="m3-icon-button m3-icon-button-danger" 
              onClick={(e) => { e.stopPropagation(); onDelete(okr._id); }}
              title="Delete"
            >
              <FaTrash />
            </button>
          )}
        </div>
      </div>
      
      {/* Middle Row - Progress Bar (full width) and Score (right aligned) */}
      <div className="m3-objective-metrics">
        <div className="m3-progress-container">
          <div 
            className="m3-progress-bar"
            style={{ width: '100%' }}
          >
            <div 
              className="m3-progress-fill"
              style={{ 
                width: `${progress}%`,
                background: meta.color || '#757575'
              }}
            />
          </div>
          <span className="m3-progress-label">{progress}%</span>
        </div>
        <div 
          className="m3-score-badge"
          style={{ 
            borderColor: scoreColor,
            color: scoreColor
          }}
        >
          Score: {okr.score?.toFixed(2) || '0.00'} ({getScoreLabel(okr.score || 0)})
        </div>
      </div>
      
      {/* Bottom Row - Expand/Collapse */}
      <div className="m3-objective-expand-row">
        <button 
          className="m3-expand-button"
          onClick={() => onToggleExpand(okr._id)}
        >
          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
          <span>{isExpanded ? 'Hide' : 'Show'} Key Results ({total})</span>
        </button>
      </div>
      
      {/* Key Results Section */}
      {isExpanded && total > 0 && (
        <div className="m3-kr-section">
          <div className="m3-kr-summary">
            {done} of {total} Key Results Completed ({Math.round((done / total) * 100)}%)
          </div>
          <div className="m3-kr-list">
            {okr.keyResults.map((kr, i) => (
              <div key={i} className="m3-kr-row">
                <div className="m3-kr-content">
                  <input
                    type="checkbox"
                    checked={kr.completed || false}
                    onChange={() => onToggleKR && onToggleKR(okr._id, i)}
                    className="m3-checkbox"
                  />
                  <span className={`m3-kr-text ${kr.completed ? 'completed' : ''}`}>
                    {kr.text}
                  </span>
                </div>
                <div className="m3-kr-meta">
                  {kr.deadline && (
                    <span className="m3-kr-deadline">
                      <FaClock style={{ fontSize: '12px', marginRight: '4px', color: deadlineColor }} />
                      {formatDate(kr.deadline)}
                    </span>
                  )}
                  {kr.team && (
                    <span className="m3-kr-team">{kr.team}</span>
                  )}
                  {kr.krOwner && (
                    <span className="m3-kr-owner">{kr.krOwner}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

